import React, { useEffect, useRef } from "react";

const SKY_TOP = "#05001a";
const SKY_MID = "#120033";
const SKY_HORIZON = "#3d1066";
const NEON_PINK = "#ff2bd6";
const NEON_MAGENTA = "#ff1493";
const NEON_CYAN = "#00f5ff";
const MOUNTAIN_FILL = "#12001f";
const GRID_GROUND = "#0a0018";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function rgbaColor(color, alpha) {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function createStars(count, width, height, horizon) {
  return Array.from({ length: count }, () => ({
    x: Math.random() * width,
    y: Math.random() * horizon * 0.92,
    r: Math.random() * 1.4 + 0.4,
    twinkle: Math.random() * Math.PI * 2,
    speed: Math.random() * 0.02 + 0.008,
  }));
}

function leftMountainOutline(width, horizonY) {
  return [
    [0, horizonY + 2],
    [0, horizonY * 0.52],
    [width * 0.06, horizonY * 0.58],
    [width * 0.11, horizonY * 0.38],
    [width * 0.17, horizonY * 0.5],
    [width * 0.23, horizonY * 0.34],
    [width * 0.3, horizonY * 0.46],
    [width * 0.36, horizonY * 0.4],
    [width * 0.42, horizonY * 0.52],
    [width * 0.48, horizonY + 1],
  ];
}

function rightMountainOutline(width, horizonY) {
  return leftMountainOutline(width, horizonY).map(([x, y]) => [width - x, y]);
}

function drawPolygon(ctx, points, fill) {
  if (points.length < 3) return;
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
}

function strokeWireframe(ctx, points, glowColor) {
  ctx.save();
  ctx.strokeStyle = glowColor;
  ctx.lineWidth = 1.5;
  ctx.shadowColor = glowColor;
  ctx.shadowBlur = 10;
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i += 1) {
    ctx.lineTo(points[i][0], points[i][1]);
  }
  ctx.stroke();

  const baseY = points[0][1];
  for (let i = 1; i < points.length - 1; i += 1) {
    ctx.beginPath();
    ctx.moveTo(points[i][0], points[i][1]);
    ctx.lineTo(points[i][0], baseY);
    ctx.stroke();

    if (i > 1) {
      ctx.beginPath();
      ctx.moveTo(points[i][0], points[i][1]);
      ctx.lineTo(points[i - 1][0], baseY);
      ctx.stroke();
    }
  }
  ctx.restore();
}

function drawSky(ctx, width, height, horizonY) {
  const grad = ctx.createLinearGradient(0, 0, 0, horizonY);
  grad.addColorStop(0, SKY_TOP);
  grad.addColorStop(0.45, SKY_MID);
  grad.addColorStop(1, SKY_HORIZON);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, horizonY + 2);
}

function drawStars(ctx, stars, time, animate) {
  for (const star of stars) {
    const pulse = animate
      ? 0.45 + Math.sin(time * star.speed + star.twinkle) * 0.35
      : 0.65;
    ctx.globalAlpha = pulse;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawSun(ctx, cx, cy, radius) {
  ctx.save();
  ctx.shadowColor = NEON_MAGENTA;
  ctx.shadowBlur = radius * 0.55;

  const grad = ctx.createLinearGradient(cx, cy - radius, cx, cy + radius * 0.15);
  grad.addColorStop(0, "#ffe566");
  grad.addColorStop(0.35, "#ff6ec7");
  grad.addColorStop(0.7, NEON_MAGENTA);
  grad.addColorStop(1, "#c71585");

  ctx.beginPath();
  ctx.arc(cx, cy, radius, Math.PI, 0);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.clip();
  const stripeGap = Math.max(5, radius * 0.045);
  const stripeH = Math.max(3, radius * 0.028);
  for (let y = cy - radius; y <= cy + stripeH; y += stripeGap) {
    ctx.fillStyle = "rgba(5, 0, 26, 0.92)";
    ctx.fillRect(cx - radius - 2, y, radius * 2 + 4, stripeH);
  }
  ctx.restore();
}

function drawGrid(ctx, width, height, horizonY, offset, animate) {
  const cx = width / 2;
  const groundH = height - horizonY;

  const groundGrad = ctx.createLinearGradient(0, horizonY, 0, height);
  groundGrad.addColorStop(0, "#1a0533");
  groundGrad.addColorStop(1, GRID_GROUND);
  ctx.fillStyle = groundGrad;
  ctx.fillRect(0, horizonY, width, groundH);

  const vLines = Math.min(28, Math.floor(width / 42));
  ctx.save();
  ctx.shadowColor = NEON_PINK;
  ctx.shadowBlur = 6;

  for (let i = -vLines; i <= vLines; i += 1) {
    const t = i / vLines;
    const bottomX = cx + t * width * 0.92;
    const alpha = 0.25 + (1 - Math.abs(t)) * 0.55;
    ctx.strokeStyle = rgbaColor(NEON_PINK, alpha);
    ctx.lineWidth = i === 0 ? 2 : 1;
    ctx.beginPath();
    ctx.moveTo(cx, horizonY);
    ctx.lineTo(bottomX, height);
    ctx.stroke();
  }
  ctx.restore();

  const hLines = 22;
  const scroll = animate ? offset : 0;

  for (let i = 0; i < hLines; i += 1) {
    const depth = ((i + scroll) % hLines) / hLines;
    const y = horizonY + groundH * depth * depth;
    const spread = depth * depth;
    const leftX = cx - spread * width * 0.95;
    const rightX = cx + spread * width * 0.95;
    const alpha = 0.12 + depth * 0.78;

    ctx.strokeStyle = rgbaColor(NEON_MAGENTA, alpha);
    ctx.lineWidth = 0.8 + depth * 2.2;
    ctx.shadowColor = NEON_MAGENTA;
    ctx.shadowBlur = depth > 0.3 ? 8 : 0;
    ctx.beginPath();
    ctx.moveTo(leftX, y);
    ctx.lineTo(rightX, y);
    ctx.stroke();
  }
  ctx.shadowBlur = 0;
}

export default function KidsAnimatedBackground() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let horizonY = 0;
    let stars = [];
    let gridOffset = 0;
    let startTime = performance.now();
    const reduced = prefersReducedMotion();

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      horizonY = height * 0.56;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const starCount = reduced
        ? 40
        : Math.min(160, Math.max(60, Math.floor((width * horizonY) / 12000)));
      stars = createStars(starCount, width, height, horizonY);
    }

    function drawFrame(time) {
      const elapsed = (time - startTime) / 1000;
      const animate = !reduced;

      ctx.clearRect(0, 0, width, height);

      drawSky(ctx, width, height, horizonY);
      drawStars(ctx, stars, elapsed, animate);

      const sunRadius = Math.min(width * 0.22, height * 0.28);
      drawSun(ctx, width / 2, horizonY, sunRadius);

      const leftMountain = leftMountainOutline(width, horizonY);
      const rightMountain = rightMountainOutline(width, horizonY);
      drawPolygon(ctx, leftMountain, MOUNTAIN_FILL);
      drawPolygon(ctx, rightMountain, MOUNTAIN_FILL);
      strokeWireframe(ctx, leftMountain, NEON_CYAN);
      strokeWireframe(ctx, rightMountain, NEON_CYAN);

      if (animate) {
        gridOffset += 0.018;
      }

      drawGrid(ctx, width, height, horizonY, gridOffset, animate);

      rafRef.current = requestAnimationFrame(drawFrame);
    }

    resize();
    rafRef.current = requestAnimationFrame(drawFrame);

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="kids-animated-bg" aria-hidden="true">
      <canvas ref={canvasRef} className="kids-animated-bg-canvas" />
      <div className="kids-animated-bg-vignette" />
    </div>
  );
}
